import { useEffect, useMemo, useState } from "react"
import type { ComponentProps, FormEvent } from "react"
import Select from "@/v2-ui/select"
import BulkForm, {
  type BulkRow,
  type BulkRowsOptions,
  useBulkRows,
} from "@/v2-backoffice-client/components/bulkForm"
import UserCreateManyTable from "@/v2-backoffice-client/user/create/many/UserCreateManyTable"
import InvitationCreateProjectFields from "@/v2-backoffice-client/invitation/create/InvitationCreateProjectFields"
import {
  type InvitationProjectValues,
  useInvitationProjectValues,
} from "@/v2-backoffice-client/invitation/create/invitation.create.hooks"
import CrmCreateContactCheckbox from "@/v2-backoffice-client/crm/CrmCreateContactCheckbox"
import { useContactEmailChecks } from "@/v2-backoffice-client/crm/crm.contactChecks.hooks"
import { DEFAULT_TENANT_USER_TYPE_CODE } from "@/v2-backoffice-client/tenant/user/tenant.user.constants"
import {
  DEFAULT_LANGUAGE,
  LANGUAGE_OPTIONS,
} from "@/v2-backoffice-client/invitation/invitation.constants"

export type UserCreateRow = BulkRow<{
  Email: string
  FirstName: string
  LastName: string
  TenantId?: number
  TenantName?: string
  UserTenantTypeCode: number
}>

export type UserCreateManyFormData = InvitationProjectValues & {
  rows: UserCreateRow[]
  // One language for the whole batch (the users' initial app language).
  Language: string
  // Create HubSpot contacts for rows that have none (existing ones are
  // always linked).
  CreateContact: boolean
}

export type UserCreateManyFormProps = Omit<
  ComponentProps<"form">,
  "onSubmit"
> & {
  defaultTenantId?: number
  defaultTenantName?: string
  onSubmit(data: UserCreateManyFormData, e: FormEvent<HTMLFormElement>): void
}

function UserCreateManyForm(props: UserCreateManyFormProps) {
  const {
    onSubmit,
    defaultTenantId,
    defaultTenantName,
    className,
    ...formProps
  } = props

  const bulk = useBulkRows(
    useMemo<BulkRowsOptions<UserCreateRow>>(
      () => ({
        createEmptyRow() {
          return {
            key: crypto.randomUUID(),
            Email: "",
            FirstName: "",
            LastName: "",
            TenantId: defaultTenantId,
            TenantName: defaultTenantName,
            UserTenantTypeCode: DEFAULT_TENANT_USER_TYPE_CODE,
          }
        },
      }),
      [defaultTenantId, defaultTenantName],
    ),
  )

  const [language, setLanguage] = useState(DEFAULT_LANGUAGE)
  const [createContact, setCreateContact] = useState(true)
  const project = useInvitationProjectValues()

  // One batched Backoffice + HubSpot check for every complete email in the
  // table; the rows and the checkbox below read from it.
  const checks = useContactEmailChecks(bulk.rows.map((row) => row.Email))

  const validRows = bulk.rows.filter((row) => {
    return (
      row.Email.trim().length > 0 &&
      row.FirstName.trim().length > 0 &&
      row.LastName.trim().length > 0
    )
  })

  // Project choices are team-scoped, so the selector only shows when every
  // row that names a team names the SAME one (the near-universal case) —
  // mixed-team batches fall back to each team's own defaults, exactly like
  // multi-team invitations do.
  const rowTenantIds = [
    ...new Set(
      bulk.rows
        .map((row) => row.TenantId)
        .filter((id): id is number => id !== undefined),
    ),
  ]
  const singleTenantId = rowTenantIds.length === 1 ? rowTenantIds[0] : undefined

  // Its choices are only meaningful against one team's project set: when
  // the batch's team changes (or stops being single), drop them back to
  // Automatic rather than carrying a stale override across teams.
  const { reset: resetProject } = project
  useEffect(() => {
    resetProject()
  }, [singleTenantId, resetProject])

  return (
    <BulkForm
      className={className}
      onPaste={bulk.handlePaste}
      onSubmit={(e) => {
        onSubmit(
          {
            rows: validRows,
            Language: language,
            CreateContact: createContact,
            ...project.values,
          },
          e,
        )
      }}
      {...formProps}
    >
      <span className="bulk-form-actions-hint">
        Paste a list of emails separated by commas, spaces, or line breaks to
        populate multiple rows at once.
      </span>

      <BulkForm.Actions
        onAddRow={bulk.addRow}
        onReset={bulk.resetRows}
        count={validRows.length}
        countLabel="user(s)"
        warning={
          validRows.length === 0
            ? "Add at least one complete user (email, first name, last name)"
            : undefined
        }
      />

      <UserCreateManyTable
        rows={bulk.rows}
        onUpdateRow={bulk.updateRow}
        onRemoveRow={bulk.removeRow}
        checks={checks}
        createContact={createContact}
      />

      {/* A fixed slot, so the warning appearing never moves the form. */}
      <span className="bulk-form-warning block h-5 truncate">
        {checks.existingUserCount === 1
          ? "1 email already belongs to a Backoffice user — remove that row (or open the user) to avoid a duplicate account."
          : checks.existingUserCount > 1
            ? `${checks.existingUserCount} emails already belong to Backoffice users — remove those rows to avoid duplicate accounts.`
            : ""}
      </span>

      <CrmCreateContactCheckbox
        className="rounded-md border border-border px-3"
        checked={createContact}
        onCheckedChange={setCreateContact}
        counts={
          checks.answered
            ? {
                new: checks.newCount,
                contacts: checks.existingContactCount,
                users: checks.existingUserCount,
              }
            : undefined
        }
      />

      {singleTenantId !== undefined && (
        <InvitationCreateProjectFields
          className="invitation-create-form-project-section"
          subject="created"
          tenantId={singleTenantId}
          projectId={project.projectId}
          onProjectIdChange={project.setProjectId}
          autoCreateProject={project.autoCreateProject}
          onAutoCreateProjectChange={project.setAutoCreateProject}
          countryCode={project.countryCode}
          onCountryCodeChange={project.setCountryCode}
        />
      )}

      <div
        className="flex items-center gap-2 text-sm whitespace-nowrap text-muted-foreground"
        title="The users' initial app language (Configuration.ActiveLanguageCode); applies to every user in the batch."
      >
        Language
        <Select value={language} onValueChange={setLanguage}>
          <Select.Trigger size="xs" className="min-w-[120px]">
            <Select.Value />
          </Select.Trigger>
          <Select.Content>
            {LANGUAGE_OPTIONS.map((opt) => (
              <Select.Item key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      </div>
    </BulkForm>
  )
}

export default UserCreateManyForm
